import * as React from "react";
import { useGlobalModalStack } from "../../global-modal-stack/index.js";
import { useGlobalDrawerStack } from "../../global-drawer-stack.js";

/**
 * Hook to manage ResponsiveModal state
 * Handles both global modal stack and controlled state fallback
 */
export function useResponsiveModalState(
  globalModalId: string | undefined,
  propOpen: boolean | undefined,
  propOnOpenChange: ((open: boolean) => void) | undefined
) {
  // Try to get global modal stack (may not be available in all contexts)
  let globalModalStack: ReturnType<typeof useGlobalModalStack> | null = null;
  try {
    globalModalStack = useGlobalModalStack();
  } catch (e) {
    // Global modal stack not available, will use controlled state
  }

  // Try to get global drawer stack (may not be available in all contexts)
  let globalDrawerStack: ReturnType<typeof useGlobalDrawerStack> | null = null;
  try {
    globalDrawerStack = useGlobalDrawerStack();
  } catch (e) {
    // Global drawer stack not available, will use controlled state
  }

  // Determine which state management to use
  const useGlobalStack = !!globalModalId && !!globalModalStack;
  
  const open = useGlobalStack 
    ? globalModalStack!.isModalOpen(globalModalId) 
    : (propOpen ?? false);
  
  const onOpenChange = useGlobalStack 
    ? (newOpen: boolean) => {
        if (newOpen) {
          globalModalStack!.openModal(globalModalId);
        } else {
          globalModalStack!.closeModal(globalModalId);
        }
        // Also call the prop callback if provided
        propOnOpenChange?.(newOpen);
      }
    : (propOnOpenChange ?? (() => {}));

  return {
    open,
    onOpenChange,
    useGlobalStack,
    globalModalStack,
    globalDrawerStack,
  };
}
